import React, { useState } from 'react';
import CanvasJSReact from '@canvasjs/react-charts';
import { DELIVERY_SERIES_COLOR, STATE_GREY_FILL, STATE_GREY_HATCH, STATE_GREY_SWATCH_OOS, fmtDay, withAlpha } from '../lib/chartTheme';
import { RESPONDED_SCORE_THRESHOLD } from '../lib/deliverySummaryV1';
import { DAY_MS } from '../lib/recordWindow';

const THRESHOLD_PCT = Math.round(RESPONDED_SCORE_THRESHOLD * 100);

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const HATCH = `repeating-linear-gradient(45deg, ${STATE_GREY_HATCH} 0 1px, transparent 1px 5px)`;

/** Alpha for a graded cell: floor at 0.35 so a single period still registers, cap at 0.9. */
const grade = share => 0.35 + 0.55 * Math.max(0, Math.min(1, share));

/**
 * Above this share of activated periods missed, the day reads red rather than a paler green.
 * A cell is the day's dominant story: one missed quarter-hour in ninety is a 99 % day, and
 * painting it red would make a month of delivery look like a month of trouble. The miss is
 * still in the tooltip, and the weekly bars draw every missed hour red.
 */
const MISSED_ALERT_SHARE = 0.1;

/**
 * The day's fill, by the story that matters most: a material miss, then how much of what was
 * activated responded, then a quiet backed day, then the non-measured states (out of service
 * before unmeasured), then nothing.
 *
 * @param {import('../lib/deliveryDashboardV1').DayAggregate} d
 */
export const cellFill = d => {
    if (d.activated > 0 && d.missed / d.activated >= MISSED_ALERT_SHARE) {
        return withAlpha(DELIVERY_SERIES_COLOR.missed, grade(d.missed / d.activated));
    }
    if (d.activated > 0) {
        return withAlpha(DELIVERY_SERIES_COLOR.responded, grade(d.responded / d.activated));
    }
    if (d.standby > 0) {
        return DELIVERY_SERIES_COLOR.standby;
    }
    if (d.outOfService > 0 && d.outOfService >= d.unmeasured) {
        return STATE_GREY_FILL;
    }
    if (d.unmeasured > 0) {
        return HATCH;
    }
    return '#eef0f2';
};

const pct = (n, of) => (of > 0 ? `${((n / of) * 100).toFixed(0)} %` : '—');

/** The tooltip body and the cell's accessible name, one text. */
export const dayLines = d => {
    if (d.periods === 0) {
        return [`${fmtDay(d.day)} · no report`];
    }
    const lines = [
        fmtDay(d.day),
        `${d.committed} committed periods · ${pct(d.backed, d.committed)} backed`,
        d.activated > 0
            ? `${d.activated} FCR activated · ${d.responded} of ${d.activated} exceed a setpoint following score of ${THRESHOLD_PCT} %`
            : d.standby > 0
            ? `never FCR activated · ${d.standby} standing by`
            : 'never FCR activated',
        `${d.capacityHours.toFixed(1)} MW·h sold`,
    ];
    if (d.outOfService > 0) {
        lines.push(`${d.outOfService} out of service`);
    }
    if (d.unmeasured > 0) {
        lines.push(`${d.unmeasured} unmeasured`);
    }
    return lines;
};

// The bar chart's axis gutters. The calendar's cell area is padded by the same amounts so its
// week columns sit over the bars for the same weeks — the two visuals share their x axis.
const AXIS_LEFT_PX = 64;
const AXIS_RIGHT_PX = 48;

const DayCalendar = ({ weeks }) => {
    const [hover, setHover] = useState(null);

    // Month ticks: the first column, and every column whose Monday starts a new month.
    const monthTicks = weeks.map((w, i) => {
        const m = new Date(w.week).getUTCMonth();
        const prev = i === 0 ? -1 : new Date(weeks[i - 1].week).getUTCMonth();
        return m !== prev ? fmtDay(w.week).slice(0, 3) : null;
    });

    return (
        <div className="vb-calendar" onMouseLeave={() => setHover(null)}>
            <div
                className="vb-calendar__grid"
                role="grid"
                aria-label="Delivery by day"
                style={{ gridTemplateColumns: `${AXIS_LEFT_PX}px repeat(${weeks.length}, minmax(0, 1fr))`, paddingRight: AXIS_RIGHT_PX }}
            >
                {monthTicks.map((label, c) => (
                    <div key={`m-${c}`} aria-hidden className="vb-calendar__month" style={{ gridColumn: c + 2, gridRow: 1 }}>
                        {label}
                    </div>
                ))}
                {WEEKDAYS.map((w, r) => (
                    <div key={`w-${r}`} aria-hidden className="vb-calendar__weekday" style={{ gridColumn: 1, gridRow: r + 2 }}>
                        {w}
                    </div>
                ))}
                {weeks.map((w, c) =>
                    w.days.map(d => {
                        const r = Math.round((d.day - w.week) / DAY_MS);
                        const lines = dayLines(d);
                        return (
                            <button
                                key={d.day}
                                type="button"
                                role="gridcell"
                                aria-label={lines.join('. ')}
                                className="vb-calendar__cell"
                                style={{ gridColumn: c + 2, gridRow: r + 2, background: cellFill(d) }}
                                onMouseEnter={e =>
                                    setHover({
                                        day: d,
                                        x: e.currentTarget.offsetLeft + e.currentTarget.offsetWidth / 2,
                                        y: e.currentTarget.offsetTop + e.currentTarget.offsetHeight + 6,
                                    })
                                }
                                onFocus={e =>
                                    setHover({
                                        day: d,
                                        x: e.currentTarget.offsetLeft + e.currentTarget.offsetWidth / 2,
                                        y: e.currentTarget.offsetTop + e.currentTarget.offsetHeight + 6,
                                    })
                                }
                                onBlur={() => setHover(null)}
                            />
                        );
                    })
                )}
            </div>
            {hover ? (
                <div role="tooltip" className="vb-calendar__tip" style={{ left: hover.x, top: hover.y }}>
                    {dayLines(hover.day).map((line, i) => (
                        <div key={i} className={i === 0 ? 'vb-calendar__tip-head' : undefined}>
                            {line}
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

/**
 * Capacity-hours sold per ISO week, stacked by what became of them — Responded (activated,
 * score over the bar), Standing by (never activated), Missed (activated, score under the
 * bar), Not backed — with the week's responded share as a line on the right axis. Exported
 * for the tests.
 */
export const weeklySeries = weeks => {
    const bar = (name, key, color) => ({
        type: 'stackedColumn',
        name,
        color,
        showInLegend: true,
        toolTipContent: '{name}: {y} MW·h',
        dataPoints: weeks.map(w => ({ label: fmtDay(w.week), y: Math.round(w[key] * 100) / 100 })),
    });
    return [
        bar('Responded', 'respondedHours', DELIVERY_SERIES_COLOR.responded),
        bar('Standing by', 'standbyHours', DELIVERY_SERIES_COLOR.standby),
        bar('Missed', 'missedHours', DELIVERY_SERIES_COLOR.missed),
        bar('Not backed', 'notBackedHours', STATE_GREY_SWATCH_OOS),
        {
            type: 'line',
            name: 'Responded share',
            color: DELIVERY_SERIES_COLOR.score,
            axisYType: 'secondary',
            lineThickness: 1.5,
            markerSize: 5,
            markerType: 'circle',
            showInLegend: true,
            toolTipContent: '{name}: {y} %',
            dataPoints: weeks.map(w => ({ label: fmtDay(w.week), y: w.respondedShare === null ? null : Math.round(w.respondedShare * 10) / 10 })),
        },
    ];
};

const WeeklyBars = ({ weeks }) => {
    const options = {
        height: 200,
        animationEnabled: false,
        axisX: { labelFontSize: 11, lineThickness: 1, tickLength: 0, interval: 1 },
        axisY: { title: 'MW·h', minimum: 0, lineThickness: 1, gridThickness: 1, gridColor: '#eef0f2', labelFontSize: 11 },
        axisY2: { title: '%', minimum: 0, maximum: 100, interval: 50, lineThickness: 1, gridThickness: 0, labelFontSize: 11 },
        legend: { fontSize: 12 },
        toolTip: { shared: true },
        dataPointWidth: 28,
        data: weeklySeries(weeks),
    };
    return <CanvasJSChart options={options} />;
};

/**
 * The 30 d / 90 d delivery dashboard: a day calendar over weekly capacity-hour bars. The
 * calendar answers "every day, how did it go"; the bars answer "what did we sell each week
 * and what became of it". Both draw the same day aggregates the tiles above are summarised
 * from, so nothing on the panel contradicts anything else.
 */
const DeliveryDashboard = ({ weeks }) => {
    if (weeks.length === 0) {
        return null;
    }
    return (
        <div className="vb-dashboard">
            <DayCalendar weeks={weeks} />
            <WeeklyBars weeks={weeks} />
        </div>
    );
};

export default DeliveryDashboard;
